import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Alert, AlertDescription } from '../ui/alert';
import { Badge } from '../ui/badge';
import { Spinner } from '../ui/spinner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Mail, AlertCircle, CheckCircle2, XCircle, Eye, Code, Languages, X } from 'lucide-react';
import crmEmailAPI from '../../services/crmEmailService';

// The CRM email translation workbench: pull a Customer.io campaign email by its two ids, pick the
// languages, translate through bi-pepper and publish each language variant straight back to
// Customer.io. Publishing is immediate — there is no draft step — so the one guard is the confirm
// dialog before the run.
export default function CrmEmailPage() {
  const [automationId, setAutomationId] = useState('');
  const [actionId, setActionId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [email, setEmail] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [showRaw, setShowRaw] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [results, setResults] = useState(null);

  const canLoad = automationId.trim() !== '' && actionId.trim() !== '';

  const load = async () => {
    if (!canLoad || loading) return;
    setLoading(true);
    setError('');
    setResults(null);
    try {
      const { data } = await crmEmailAPI.getEmail(automationId.trim(), actionId.trim());
      setEmail(data);
      setSelected(new Set());
      setShowRaw(false);
    } catch (err) {
      setEmail(null);
      setError(err.response?.data?.error || 'Failed to load the email');
    } finally {
      setLoading(false);
    }
  };

  // The dropdown only ever adds: picking a language that is already selected must not quietly
  // drop it from the run. Removing is the chip's X button.
  const addLanguage = (lang) => {
    setSelected((prev) => (prev.has(lang) ? prev : new Set(prev).add(lang)));
  };

  const removeLanguage = (lang) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.delete(lang);
      return next;
    });
  };

  const translate = async () => {
    if (!email || selected.size === 0 || translating) return;
    setConfirmOpen(false);
    setTranslating(true);
    setError('');
    setResults(null);
    try {
      const { data } = await crmEmailAPI.translate({
        automation_id: email.automation_id,
        action_id: email.action_id,
        languages: [...selected],
      });
      setResults(data.results || []);
      // A published language now exists on the action; refresh so the checkboxes say so.
      try {
        const refreshed = await crmEmailAPI.getEmail(email.automation_id, email.action_id);
        setEmail(refreshed.data);
      } catch {
        // The run's outcome is already in hand; a stale "exists" chip is cosmetic.
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Translation failed');
    } finally {
      setTranslating(false);
    }
  };

  return (
    <div className="p-6 space-y-6 max-w-4xl">
      <div>
        <h1 className="font-display text-2xl font-bold text-ink flex items-center gap-2">
          <Mail className="h-5 w-5 text-primary-500" /> CRM emails
        </h1>
        <p className="text-sm text-ink-dim mt-1 font-medium">
          Translate a Customer.io campaign email: enter the automation and action ids, pick the
          languages, and each translation is published to Customer.io the moment it is done.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Campaign email</CardTitle>
        </CardHeader>
        <CardContent className="flex items-end gap-3">
          <div className="w-40">
            <label htmlFor="crm-automation-id" className="text-sm text-ink-dim font-medium">
              Automation ID
            </label>
            <Input
              id="crm-automation-id"
              value={automationId}
              placeholder="12"
              onChange={(e) => setAutomationId(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && load()}
            />
          </div>
          <div className="w-40">
            <label htmlFor="crm-action-id" className="text-sm text-ink-dim font-medium">
              Action ID
            </label>
            <Input
              id="crm-action-id"
              value={actionId}
              placeholder="34"
              onChange={(e) => setActionId(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && load()}
            />
          </div>
          <Button onClick={load} disabled={!canLoad || loading}>
            {loading ? 'Loading…' : 'Load'}
          </Button>
        </CardContent>
      </Card>

      {error && (
        <Alert variant="error">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {loading && !email && <Spinner />}

      {email && (
        <>
          <Card>
            <CardHeader className="flex-row items-center justify-between space-y-0">
              <CardTitle className="text-base">{email.name || 'Campaign action'}</CardTitle>
              <div className="flex items-center gap-2">
                {email.sending_state && (
                  <Badge variant="secondary">{email.sending_state}</Badge>
                )}
                <Button variant="ghost" size="sm" onClick={() => setShowRaw((v) => !v)}
                  aria-label={showRaw ? 'Show preview' : 'Show HTML source'}>
                  {showRaw ? <Eye className="h-4 w-4" /> : <Code className="h-4 w-4" />}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <div className="text-xs text-ink-mute font-medium">Subject</div>
                <div className="text-sm font-bold text-ink">{email.subject || '—'}</div>
              </div>
              <div>
                <div className="text-xs text-ink-mute font-medium">Preheader</div>
                <div className="text-sm text-ink-dim font-medium">{email.preheader_text || '—'}</div>
              </div>
              <div>
                <div className="text-xs text-ink-mute font-medium mb-1">Body</div>
                {showRaw ? (
                  <pre className="max-h-80 overflow-auto rounded-lg border border-white/10 bg-surface-2 p-3 text-xs font-mono text-ink-dim whitespace-pre-wrap break-all">
                    {email.body}
                  </pre>
                ) : (
                  <iframe
                    title="Email body preview"
                    sandbox=""
                    srcDoc={email.body}
                    className="w-full h-80 rounded-lg border border-white/10 bg-white"
                  />
                )}
              </div>
              {!email.has_inc_markers && (
                <Alert variant="warning">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    This body has no &lt;inc&gt; translation markers. bi-pepper refuses to translate
                    it — mark the translatable runs in the Customer.io template first.
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Languages className="h-4 w-4" /> Languages
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {email.languages.length === 0 ? (
                <p className="text-sm text-ink-dim font-medium">This brand has no languages configured.</p>
              ) : (
                <>
                  <div className="max-w-56">
                    <Select value="" onValueChange={addLanguage}>
                      <SelectTrigger aria-label="Add a language">
                        <SelectValue placeholder="Add a language…" />
                      </SelectTrigger>
                      <SelectContent>
                        {email.languages.map((lang) => (
                          <SelectItem key={lang.language} value={lang.language}>
                            {lang.language}
                            {!lang.exists && !lang.error ? ' — no template' : ''}
                            {lang.error ? ' — unknown' : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {selected.size > 0 && (
                    <ul className="flex flex-wrap gap-2">
                      {[...selected].map((code) => {
                        const lang = email.languages.find((x) => x.language === code);
                        return (
                          <li
                            key={code}
                            className="flex items-center gap-2 rounded-lg border border-white/10 px-3 py-1.5"
                          >
                            <span className="text-sm font-bold text-ink">{code}</span>
                            {lang?.error ? (
                              <Badge variant="destructive" title={lang.error}>unknown</Badge>
                            ) : lang?.exists ? (
                              <Badge variant="success">template ready</Badge>
                            ) : (
                              <Badge variant="warning">no template</Badge>
                            )}
                            <button
                              type="button"
                              onClick={() => removeLanguage(code)}
                              className="text-ink-mute hover:text-ink"
                              aria-label={`Remove ${code}`}
                            >
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                  {[...selected].some((code) => {
                    const lang = email.languages.find((x) => x.language === code);
                    return lang && !lang.exists && !lang.error;
                  }) && (
                    <p className="text-xs text-warning font-medium">
                      A language with no template cannot be translated — add the language in
                      Customer.io first, then load the email again.
                    </p>
                  )}
                </>
              )}
              <Button
                onClick={() => setConfirmOpen(true)}
                disabled={selected.size === 0 || translating || !email.has_inc_markers}
              >
                {translating ? <Spinner size="sm" /> : 'Translate'}
              </Button>
            </CardContent>
          </Card>
        </>
      )}

      {results && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Results</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="divide-y divide-white/10">
              {results.map((result) => (
                <li key={result.language} className="flex items-start justify-between py-3 gap-4">
                  <div className="min-w-0">
                    <div className="text-sm font-bold text-ink">{result.language}</div>
                    {result.published ? (
                      <div className="text-xs text-ink-mute font-medium">
                        {result.agent_id
                          ? `Published via ${result.agent_id} · ${result.email_translation_id}`
                          : 'Published without translation, markers removed'}
                      </div>
                    ) : (
                      <div className="text-xs text-negative font-medium break-words">{result.error}</div>
                    )}
                  </div>
                  {result.published ? (
                    <CheckCircle2 className="h-4 w-4 text-positive shrink-0" />
                  ) : (
                    <XCircle className="h-4 w-4 text-negative shrink-0" />
                  )}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      <Dialog open={confirmOpen} onOpenChange={(v) => !translating && setConfirmOpen(v)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Publish to Customer.io?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-ink-dim font-medium">
            {[...selected].join(', ')} will be translated and written to Customer.io immediately —
            there is no draft step. Existing variants of a selected language are overwritten.
          </p>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button size="sm" onClick={translate}>Translate &amp; publish</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
